import { useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { useEvent } from '../hooks/useEvents'
import { usePresentationTimetable, useServiceInfo } from '../hooks/useServices'
import { useBriefingTickets } from '../hooks/useTicketTypes'
import { useBriefingDoc } from '../hooks/useBriefingDoc'
import { formatEventDates } from '../lib/dates'
import { formatGBP } from '../lib/money'
import { PrintShell, Section, Table, Empty, usePrintOnReady } from '../components/print/printKit'
import type { PresTimetableRow } from '../types'

// EventProductBriefing (Feature 1) — the rich, branded product briefing PDF
// (like "A Bloody Merry Christmas Briefing"). Route /events/:id/product-briefing.
// Prose sections come from EventBriefingDoc (editable in the Product Briefing
// tab); the Event Schedule, Capacity and Ticket Pricing tables are pulled live
// so they never drift. Schedule comes from the presentation timetable (manual
// Services, else the full per-day MERAC projection) and pricing from the
// resolved briefing tickets (configured price, MERAC unit-price fallback).

function fmtDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC'
  })
}

export function EventProductBriefing() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = Number(idParam)
  const { data: event } = useEvent(id)
  const { data: doc } = useBriefingDoc(id)
  const { data: timetable } = usePresentationTimetable(id)
  const { data: info } = useServiceInfo(id)
  const { data: tickets } = useBriefingTickets(id)

  usePrintOnReady(!!event && doc != null && timetable != null && tickets != null)

  if (!event) return <PrintShell><p>Loading…</p></PrintShell>

  const when = formatEventDates(event.dates, event.startDate, event.endDate)
  // Event services only (Pullman/dining are scheduled separately). MERAC rows
  // are all serviceType 'event'.
  const services: PresTimetableRow[] = (timetable?.services ?? [])
    .filter((s) => s.serviceType !== 'pullman' && s.serviceType !== 'dining')
  const hasDates = services.some((s) => s.date)

  // Capacity breakdown (§5), source-aware. Manual timetables are one set of
  // trips that run every operating day; the MERAC projection is already one row
  // per session per day, so we group by date instead of multiplying.
  const totalCapacity = services.reduce((a, s) => a + (s.capacity ?? 0), 0)
  const anyCapacity = services.some((s) => (s.capacity ?? 0) > 0)
  let tripsPerDay: number
  let perDayCapacity: number
  let operatingDays: number
  let grandTotalCapacity: number
  if (hasDates) {
    const dates = new Set(services.map((s) => s.date).filter(Boolean))
    operatingDays = dates.size || 1
    tripsPerDay = Math.round(services.length / operatingDays)
    perDayCapacity = Math.round(totalCapacity / operatingDays)
    grandTotalCapacity = totalCapacity
  } else {
    tripsPerDay = services.length
    perDayCapacity = totalCapacity
    operatingDays = event.dates?.length && event.dates.length > 0 ? event.dates.length : 1
    grandTotalCapacity = perDayCapacity * operatingDays
  }
  const gb = (n: number) => n.toLocaleString('en-GB')

  return (
    <PrintShell>
      {/* Branded hero band */}
      <section style={{ background: '#0a4a3a', color: '#fff', margin: '-24px -24px 20px', padding: '32px 24px', pageBreakAfter: 'auto' }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.14em', opacity: 0.8, marginBottom: 8 }}>
          North Yorkshire Moors Railway · Product Briefing
        </div>
        <h1 style={{ color: '#fff', fontSize: 34, margin: 0 }}>{event.name}</h1>
        {doc?.heroTagline && <p style={{ fontSize: 15, marginTop: 8, marginBottom: 0, color: '#e8efe9' }}>{doc.heroTagline}</p>}
        <p style={{ fontSize: 13, marginTop: 8, marginBottom: 0, color: '#cddbd4' }}>
          {event.type.name} · {when}{doc?.subtitle ? ` · ${doc.subtitle}` : ''}
        </p>
      </section>

      {doc?.overviewMd && <Section title="1. Event overview"><ReactMarkdown>{doc.overviewMd}</ReactMarkdown></Section>}
      {doc?.conceptMd && <Section title="2. Concept &amp; theme"><ReactMarkdown>{doc.conceptMd}</ReactMarkdown></Section>}
      {doc?.guestJourneyMd && <Section title="3. Guest journey"><ReactMarkdown>{doc.guestJourneyMd}</ReactMarkdown></Section>}

      <Section title="4. Event schedule">
        {(info?.journey || info?.runTime || info?.setFormation || info?.capacityNote) && (
          <p style={{ background: '#f5f2ea', padding: '6px 10px', borderRadius: 4 }}>
            {info?.journey && <span><strong>Journey:</strong> {info.journey}&nbsp;&nbsp;</span>}
            {info?.runTime && <span><strong>Run time:</strong> {info.runTime}&nbsp;&nbsp;</span>}
            {info?.setFormation && <span><strong>Set:</strong> {info.setFormation}&nbsp;&nbsp;</span>}
            {info?.capacityNote && <span><strong>Capacity:</strong> {info.capacityNote}</span>}
          </p>
        )}
        {services.length === 0 ? <Empty>Timetable to be confirmed — see the Schedule / Service Timetable tab.</Empty> : (
          <Table headers={hasDates ? ['Date', 'Dep.', 'Arr.', 'Service', 'From → To', 'Capacity'] : ['Dep.', 'Arr.', 'Service', 'From → To', 'Capacity']}>
            {services.map((s) => (
              <tr key={s.id}>
                {hasDates && <td style={{ whiteSpace: 'nowrap' }}>{s.date ? fmtDate(s.date) : '—'}</td>}
                <td style={{ fontFamily: 'monospace' }}>{s.timeText}</td>
                <td style={{ fontFamily: 'monospace', color: '#5a5a5a' }}>{s.arrivalTime ?? '—'}</td>
                <td><strong>{s.serviceLabel}</strong></td>
                <td>{s.originStation && s.destinationStation ? `${s.originStation} → ${s.destinationStation}` : '—'}</td>
                <td>{s.capacity ?? '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="5. Capacity breakdown">
        {!anyCapacity ? (
          <Empty>Capacity to be confirmed — set a per-service capacity on the Service Timetable tab, or link the event to MERAC.</Empty>
        ) : (
          <Table headers={['Metric', 'Figure']}>
            {info?.setFormation && (
              <tr><td>Set / formation</td><td>{info.setFormation}</td></tr>
            )}
            <tr><td>Trips per operating day</td><td>{gb(tripsPerDay)}</td></tr>
            <tr><td>Maximum capacity per day</td><td>{gb(perDayCapacity)}</td></tr>
            <tr><td>Operating days</td><td>{gb(operatingDays)}</td></tr>
            <tr><td><strong>Total event capacity</strong></td><td><strong>{gb(grandTotalCapacity)}</strong></td></tr>
            <tr><td>60% planning figure — per day</td><td>{gb(Math.round(perDayCapacity * 0.6))}</td></tr>
            <tr><td>60% planning figure — full event</td><td>{gb(Math.round(grandTotalCapacity * 0.6))}</td></tr>
          </Table>
        )}
      </Section>

      <Section title="6. Ticket pricing">
        {(tickets ?? []).length === 0 ? <Empty>Ticket types to be confirmed — build them on the Ticketing tab or link the event to MERAC.</Empty> : (
          <Table headers={['Ticket type', 'Price', 'Notes']}>
            {(tickets ?? []).map((t, i) => (
              <tr key={i}>
                <td><strong>{t.name}</strong></td>
                <td>{t.pricePence == null ? 'TBC' : formatGBP(t.pricePence)}</td>
                <td>{t.notes ?? (t.isConcession ? 'Concession' : '—')}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      {doc?.ticketingNotesMd && <Section title="7. Ticketing — key information"><ReactMarkdown>{doc.ticketingNotesMd}</ReactMarkdown></Section>}
      {doc?.marketingMd && <Section title="8. Marketing — key information"><ReactMarkdown>{doc.marketingMd}</ReactMarkdown></Section>}
      {doc?.faqMd && <Section title="9. Frequently asked questions"><ReactMarkdown>{doc.faqMd}</ReactMarkdown></Section>}

      <p style={{ fontSize: 10, color: '#5a5a5a', marginTop: 30 }}>
        Generated {new Date().toLocaleString('en-GB')} · North Yorkshire Moors Railway
      </p>
    </PrintShell>
  )
}
